import React, { useEffect, useState } from 'react'
import { useHlsPlayerUI } from './HlsPlayerUIContext'

const PIP_ICON_SIZE = 20

/**
 * Inline SVG for the Picture-in-Picture icon.
 * A small rectangle inset within a larger rectangle outline,
 * representing a floating video window.
 */
const PipIcon: React.FC<{ size?: number }> = ({ size = PIP_ICON_SIZE }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
    <rect x="12" y="12" width="8" height="6" rx="1" fill="currentColor" />
  </svg>
)

/**
 * Picture-in-Picture toggle button for the control bar.
 * Only renders when the browser supports the Picture-in-Picture API.
 */
export const HlsPipButton: React.FC<{ videoRef: React.RefObject<HTMLVideoElement | null> }> = ({ videoRef }) => {
  const { controller } = useHlsPlayerUI()
  const [pipActive, setPipActive] = useState(false)
  const [pipSupported, setPipSupported] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    setPipSupported(!!video && 'requestPictureInPicture' in video)
  }, [videoRef])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const onEnter = () => setPipActive(true)
    const onLeave = () => setPipActive(false)

    video.addEventListener('enterpictureinpicture', onEnter)
    video.addEventListener('leavepictureinpicture', onLeave)
    return () => {
      video.removeEventListener('enterpictureinpicture', onEnter)
      video.removeEventListener('leavepictureinpicture', onLeave)
    }
  }, [videoRef])

  const handleClick = async () => {
    const video = videoRef.current
    if (!video || !controller) return

    try {
      if (pipActive) {
        await document.exitPictureInPicture()
      } else {
        await video.requestPictureInPicture()
      }
    } catch {
      // PiP request can fail if user gesture requirement isn't met or feature is disabled
    }
  }

  if (!pipSupported) return null

  return (
    <button
      type="button"
      className="flex h-8 w-8 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20"
      onClick={handleClick}
      aria-label={pipActive ? 'Exit picture-in-picture' : 'Enter picture-in-picture'}
    >
      <PipIcon />
    </button>
  )
}
